import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ResourceCard from '@/components/feature/ResourceCard';
import { resources, siteInfo } from '@/mocks/resources';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const article = useMemo(() => resources.find((r) => r.id === id), [id]);

  const relatedResources = useMemo(() => {
    if (!article) return [];
    return resources
      .filter((r) => r.categorySlug === article.categorySlug && r.id !== article.id)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-300 mb-4">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-800 mb-2">资源未找到</h1>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleContent = `
    <h2>资源简介</h2>
    <p>${article.description}</p>
    <p>这是一份精心整理的${article.category}资源包，经过团队严格筛选和审核，确保每份内容的质量和实用性。无论你是初学者还是资深用户，都能从中找到有价值的内容。</p>

    <h2>资源亮点</h2>
    <ul>
      <li><strong>内容丰富全面：</strong>涵盖该领域的核心内容，从基础入门到高级进阶，满足不同层次用户的需求。</li>
      <li><strong>格式规范统一：</strong>所有文件均已按照统一标准整理归类，方便查找和使用。</li>
      <li><strong>持续更新维护：</strong>资源包会跟随行业发展趋势持续更新，确保内容的时效性。</li>
      <li><strong>即装即用：</strong>下载解压后即可直接使用，无需复杂的配置和安装过程。</li>
    </ul>

    <h2>适用人群</h2>
    <p>本资源适合以下用户群体：</p>
    <ul>
      <li>正在学习相关技能的初学者</li>
      <li>需要提升工作效率的职场人士</li>
      <li>寻找灵感和参考素材的创作者</li>
      <li>准备考试或考证的学生群体</li>
    </ul>

    <h2>使用建议</h2>
    <p>建议按照资源包内的目录结构循序渐进地学习使用。如果是模板类资源，建议先浏览全部内容了解整体风格，再选择最适合自己需求的进行定制。对于工具类资源，请务必阅读使用说明文档，确保正确安装和使用。</p>

    <h2>下载说明</h2>
    <p>点击下方下载按钮即可获取完整资源包。如果遇到下载问题，可以尝试更换下载线路或选择非高峰时段下载。本资源完全免费，请放心使用。如需商用，请查看资源包内的授权说明。</p>

    <h2>更新日志</h2>
    <p>最后更新日期：${article.date}。我们会持续关注用户反馈和行业发展，定期对资源进行补充和优化，确保您始终能获取到最新、最优质的内容。</p>
  `;

  return (
    <>
      <SEO
        title={`${article.title} - ${siteInfo.name}`}
        description={article.description}
        keywords={`${article.tags.join(',')},${siteInfo.name},${article.category}下载`}
        canonicalUrl={`https://geiliziyuan.com/article/${article.id}`}
        ogType="article"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.description,
          datePublished: article.date,
          url: `https://geiliziyuan.com/article/${article.id}`,
          author: {
            '@type': 'Organization',
            name: siteInfo.name,
          },
          publisher: {
            '@type': 'Organization',
            name: siteInfo.name,
          },
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/60">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: article.category, href: `/category/${article.categorySlug}` },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <div className="flex flex-col lg:flex-row gap-8">
              <article className="flex-1 min-w-0">
                <div className="relative w-full aspect-[16/9] rounded-xl overflow-hidden mb-6">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    className="w-full h-full object-cover"
                    title={article.title}
                  />
                </div>

                <header className="mb-6">
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <Link
                      to={`/category/${article.categorySlug}`}
                      className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full hover:bg-primary-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {article.category}
                    </Link>
                    {article.tags.map((tag) => (
                      <span key={tag} className="px-2.5 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <h1 className="font-heading text-2xl md:text-3xl font-black text-foreground-950 mb-4 leading-tight">
                    {article.title}
                  </h1>

                  <div className="flex items-center gap-4 text-sm text-foreground-400 flex-wrap">
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      发布于 {article.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-download-line"></i>
                      {article.downloadCount.toLocaleString()} 次下载
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-star-fill text-primary-400"></i>
                      {article.rating} 分
                    </span>
                  </div>
                </header>

                <div
                  className="prose prose-sm md:prose-base max-w-none mb-8"
                  dangerouslySetInnerHTML={{
                    __html: articleContent
                      .replace(/<h2>/g, '<h2 class="font-heading text-xl font-bold text-foreground-900 mt-8 mb-3">')
                      .replace(/<h3>/g, '<h3 class="font-heading text-lg font-bold text-foreground-800 mt-6 mb-2">')
                      .replace(/<p>/g, '<p class="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">')
                      .replace(/<ul>/g, '<ul class="list-disc pl-5 mb-4 space-y-1">')
                      .replace(/<li>/g, '<li class="text-sm md:text-base text-foreground-600 leading-relaxed">')
                      .replace(/<li><strong>/g, '<li class="text-sm md:text-base text-foreground-600 leading-relaxed"><strong class="text-foreground-800">'),
                  }}
                />

                <div className="mt-10 pt-8 border-t border-background-200">
                  <div className="flex items-center justify-center">
                    <button className="px-10 py-4 bg-primary-500 hover:bg-primary-600 text-background-50 font-bold text-base rounded-xl transition-all duration-300 flex items-center gap-2 cursor-pointer whitespace-nowrap">
                      <i className="ri-download-cloud-2-line text-lg"></i>
                      免费下载资源
                    </button>
                  </div>
                </div>
              </article>

              <aside className="lg:w-80 flex-shrink-0">
                <div className="lg:sticky lg:top-24 space-y-6">
                  <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                    <h3 className="font-heading text-sm font-bold text-foreground-800 mb-3">资源信息</h3>
                    <ul className="space-y-2.5 text-sm text-foreground-600">
                      <li className="flex items-center justify-between">
                        <span>分类</span>
                        <Link to={`/category/${article.categorySlug}`} className="text-primary-600 font-medium cursor-pointer">{article.category}</Link>
                      </li>
                      <li className="flex items-center justify-between">
                        <span>评分</span>
                        <span className="text-primary-600 font-medium">{article.rating} / 5.0</span>
                      </li>
                      <li className="flex items-center justify-between">
                        <span>下载</span>
                        <span className="font-medium">{article.downloadCount.toLocaleString()}</span>
                      </li>
                      <li className="flex items-center justify-between">
                        <span>更新</span>
                        <span className="font-medium">{article.date}</span>
                      </li>
                    </ul>
                  </div>

                  {relatedResources.length > 0 && (
                    <div>
                      <h3 className="font-heading text-sm font-bold text-foreground-800 mb-3">相关资源</h3>
                      <div className="space-y-3">
                        {relatedResources.map((r) => (
                          <ResourceCard key={r.id} resource={r} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </aside>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}